import { useState, useEffect, useCallback } from 'react';
import { loggingService } from '../services';

/**
 * 📋 Hook para obtener historial de orden
 */
export const useOrderHistory = (orderId) => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchHistory = useCallback(async () => {
    if (!orderId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await loggingService.getInsertionOrderHistory(orderId);
      if (result.success) {
        setHistory(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Error al cargar historial de la orden');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return { history, loading, error, refetch: fetchHistory };
};

/**
 * 📋 Hook para obtener historial de asignación
 */
export const useAssignmentHistory = (assignmentId) => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchHistory = useCallback(async () => {
    if (!assignmentId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await loggingService.getBudgetAssignmentHistory(assignmentId);
      if (result.success) {
        setHistory(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Error al cargar historial de la asignación');
    } finally {
      setLoading(false);
    }
  }, [assignmentId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return { history, loading, error, refetch: fetchHistory };
};

/**
 * 📊 Hook para actividad reciente
 */
export const useRecentActivity = (limit = 20, autoRefresh = false, refreshInterval = 30000) => {
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchActivity = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await loggingService.getRecentBudgetActivity(limit);
      if (result.success) {
        setActivity(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Error al cargar actividad reciente');
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  // Auto-refresh si está habilitado
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(fetchActivity, refreshInterval);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, fetchActivity]);

  return { activity, loading, error, refetch: fetchActivity };
};

/**
 * 📈 Hook para estadísticas de actividad
 */
export const useActivityStats = (days = 30) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await loggingService.getBudgetActivityStats(days);
      if (result.success) {
        setStats(result.stats);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, loading, error, refetch: fetchStats };
};

/**
 * 📊 Hook principal para manejo de logging y auditoría
 */
export const useLogging = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Obtener historial de cualquier entidad
   */
  const getEntityHistory = useCallback(async (entityType, entityId) => {
    setLoading(true);
    setError(null);
    
    try {
      let result;
      
      if (entityType === 'INSERTION_ORDER') {
        result = await loggingService.getInsertionOrderHistory(entityId);
      } else if (entityType === 'BUDGET_ASSIGNMENT') {
        result = await loggingService.getAssignmentHistory(entityId);
      } else {
        result = await loggingService.getEntityLogs(entityType, entityId);
      }
      
      if (result.success) {
        return result.data;
      } else {
        setError(result.error);
        return null;
      }
    } catch (err) {
      setError('Error al obtener historial de la entidad');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Formatear logs para UI
   */
  const formatLogsForUI = useCallback((logs) => {
    return logs.map(log => loggingService.formatLogForUI(log));
  }, []);

  /**
   * Agrupar logs por fecha
   */
  const groupLogsByDate = useCallback((logs) => {
    return loggingService.groupLogsByDate(logs);
  }, []);

  /**
   * Filtrar logs
   */
  const filterLogs = useCallback((logs, filters) => {
    return loggingService.filterLogs(logs, filters);
  }, []);

  return {
    // Estados generales
    loading,
    error,
    
    // Métodos utilitarios
    getEntityHistory,
    formatLogsForUI,
    groupLogsByDate,
    filterLogs,
    
    // Acceso directo al servicio
    service: loggingService
  };
};

export default useLogging; 